// src/components/ui/calendar/week/WeekViewEventCard/WeekViewEventCardShadcn.jsx
import React from 'react';
import { cn } from '../../../../../utils/cn';
import { Badge } from '../../../badge';
import { EventStatusBadgeShadcn } from '../../events';
import {
  Calendar,
  Wrench,
  Network
} from 'lucide-react';

const WeekViewEventCardShadcn = ({
  event,
  onClick,
  showEventPanel = false,
  className = ''
}) => {
  const handleClick = (e) => {
    e.stopPropagation();
    if (!event.isPreview && !showEventPanel && onClick) {
      onClick(event);
    }
  };

  // Abbreviate customer name for compact display
  const getAbbreviatedName = (name) => {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}. ${parts[parts.length - 1]}`;
    }
    return name;
  };

  // Determine event type icon
  const getEventTypeIcon = () => {
    if (event.is_partner_service_event || event.is_referral_event) {
      return <Network className="w-3 h-3 flex-shrink-0" />;
    }
    if (event.event_category === 'service') {
      return <Wrench className="w-3 h-3 flex-shrink-0" />;
    }
    return <Calendar className="w-3 h-3 flex-shrink-0" />;
  };

  const isServiceEvent = event.event_category === 'service';

  return (
    <div
      className={cn(
        // Glassmorphism styling - NO absolute positioning (parent handles it)
        "rounded-lg cursor-pointer h-full p-2",
        "bg-slate-800/40 backdrop-blur-xl border border-slate-700/50",
        "hover:bg-slate-700/40 hover:border-slate-600/50",
        "transition-all duration-300 shadow-lg hover:shadow-xl flex flex-col relative overflow-hidden",
        event.isPreview && "border-dashed animate-preview-pulse",
        showEventPanel && !event.isPreview && "pointer-events-none opacity-60",
        className
      )}
      onClick={handleClick}
    >
      {/* Glassmorphism gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />

      {/* Color accent bar (left edge) */}
      <div
        className={cn(
          "absolute top-0 left-0 w-1 h-full",
          event.isPreview && "w-0.5"
        )}
        style={{ backgroundColor: event.backgroundColor }}
      />

      <div className="relative z-10 flex flex-col h-full gap-1">
        {/* Row 1: Time + Status */}
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-slate-200">
            {event.start.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}
          </span>
          {event.status && (
            <EventStatusBadgeShadcn
              status={event.status}
              size="xs"
              variant="icon-only"
              className="flex-shrink-0"
            />
          )}
        </div>

        {/* Row 2: Icon + Title */}
        <div className="flex items-center gap-1 min-w-0">
          <div style={{ color: event.backgroundColor }}>
            {getEventTypeIcon()}
          </div>
          <div className="font-bold text-xs leading-tight truncate text-slate-200">
            {event.title}
          </div>
        </div>

        {/* Row 3: Customer (if service event) */}
        {isServiceEvent && event.customer?.name && (
          <div className="text-[11px] text-slate-300 truncate">
            {getAbbreviatedName(event.customer.name)}
          </div>
        )}

        {/* Row 4: Badges (partner/preview) */}
        <div className="mt-auto flex flex-wrap gap-0.5">
          {(event.is_partner_service_event || event.is_referral_event) && (
            <Badge
              variant="outline"
              className="text-[8px] px-1 py-0 h-3.5 gap-0.5 bg-purple-500/20 text-purple-400 border-purple-500/30"
            >
              <Network className="w-2 h-2" />
            </Badge>
          )}
          {event.isPreview && (
            <Badge
              variant="outline"
              className="text-[8px] px-1 py-0 h-3.5 bg-blue-500/20 text-blue-400 border-blue-500/30"
            >
              Preview
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeekViewEventCardShadcn;